import React, { useState, useMemo, useEffect, useContext } from 'react';
import ReactECharts from 'echarts-for-react';
import { CSVLink } from 'react-csv';
import { PACKAGE_MANAGER_TYPE } from "../../../shared/utils/enum";
import {
    Card,
    Typography,
    Space,
    Button,
    Select,
    Tooltip,
    Alert,
    Row,
    Col,
    Tag,
    Radio,
    theme,
    Empty
} from "antd";
import {
    BarChartOutlined,
    PieChartOutlined,
    DollarOutlined,
    FilterOutlined,
    FileExcelOutlined,
    FullscreenOutlined,
    CompressOutlined,
    EyeOutlined,
    CloseOutlined
} from "@ant-design/icons";
import { DarkModeContext } from "../../../shared/contexts/darkModeContext";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

// --- 표시 형식 정의 ---
const DISPLAY_MODE = {
    PERCENT: 'percent',
    VALUE: 'value',
};

// --- 차트 크기 및 셀 크기 관련 상수 ---
const FIXED_CELL_WIDTH = 70;
const FIXED_CELL_HEIGHT = 35;
const Y_AXIS_LABEL_WIDTH = 120;
const X_AXIS_HEIGHT = 30;
const TITLE_HEIGHT = 60;
const CHART_RIGHT_PADDING = 20;
const CHART_BOTTOM_PADDING = 20;
const MIN_CHART_HEIGHT = 300;

// --- 데이터 가공 함수 ---
function processDataForECharts(rawData, chartType, category1Filter = null) {
    if (!rawData || rawData.length === 0) {
        return { xAxisData: [], yAxisData: [], seriesDataForChart: [], originalDataMap: new Map() };
    }

    let filteredData = rawData;
    if (category1Filter && chartType === PACKAGE_MANAGER_TYPE.RANGE) {
        filteredData = rawData.filter(item => item.category1 === category1Filter);
    }

    const xAxisData = [...new Set(filteredData.map(item => item.created.split(' ')[0]))].sort((a, b) => new Date(a) - new Date(b));
    let yAxisData = [];
    const countsByDateAndYAxis = {};
    const totalsByDate = {};

    // LEVEL 타입일 때만 날짜+레벨별 pid 중복 방지용 Set
    let pidSetByDateAndLevel = {};

    filteredData.forEach(item => {
        const date = item.created.split(' ')[0];
        let yCategory;

        switch (chartType) {
            case PACKAGE_MANAGER_TYPE.AMOUNT:
            case PACKAGE_MANAGER_TYPE.COUNT:
                yCategory = item.category1;
                break;
            case PACKAGE_MANAGER_TYPE.RANGE:
                const priceStr = item.price !== undefined ? `₩${item.price.toLocaleString()}` : '가격정보없음';
                const category2Str = item.category2 || 'N/A';
                yCategory = `${priceStr} (${category2Str})`;
                break;
            case PACKAGE_MANAGER_TYPE.LEVEL: {
                const level = item.lv;
                if (level >= 4 && level <= 15) yCategory = `Lv ${level}`;
                else if (level >= 16 && level <= 50) {
                    const startLevel = Math.floor((level - 16) / 5) * 5 + 16;
                    yCategory = `Lv ${startLevel}-${startLevel + 4}`;
                }
                else if (level >= 51 && level <= 100) {
                    const startLevel = Math.floor((level - 51) / 10) * 10 + 51;
                    yCategory = `Lv ${startLevel}-${startLevel + 9}`;
                }
                else if (level >= 101 && level <= 200) {
                    const startLevel = Math.floor((level - 101) / 20) * 20 + 101;
                    yCategory = `Lv ${startLevel}-${startLevel + 19}`;
                }
                else if (level > 200) yCategory = "Lv 201+";
                else yCategory = "Lv 1-3";
                // pid 중복 방지: 날짜+레벨별로 pid가 이미 집계되었으면 skip
                const pid = item.pid;
                if (!pidSetByDateAndLevel[date]) pidSetByDateAndLevel[date] = {};
                if (!pidSetByDateAndLevel[date][yCategory]) pidSetByDateAndLevel[date][yCategory] = new Set();
                if (pidSetByDateAndLevel[date][yCategory].has(pid)) return; // 이미 집계된 pid면 skip
                pidSetByDateAndLevel[date][yCategory].add(pid);
                break;
            }
            default:
                yCategory = item.category1;
        }

        if (!yCategory) return;

        if (!countsByDateAndYAxis[date]) countsByDateAndYAxis[date] = {};
        if (!countsByDateAndYAxis[date][yCategory]) countsByDateAndYAxis[date][yCategory] = { count: 0, amount: 0 };
        countsByDateAndYAxis[date][yCategory].count++;
        countsByDateAndYAxis[date][yCategory].amount += (item.price || 0);

        if (!totalsByDate[date]) totalsByDate[date] = { count: 0, amount: 0 };
        totalsByDate[date].count++;
        totalsByDate[date].amount += (item.price || 0);
    });

    const yCategorySet = new Set();
    Object.values(countsByDateAndYAxis).forEach(dateData =>
        Object.keys(dateData).forEach(cat => yCategorySet.add(cat))
    );

    if (chartType === PACKAGE_MANAGER_TYPE.RANGE) {
        yAxisData = [...yCategorySet].sort((a, b) => {
            const regex = /₩([\d,]+) \((.+)\)/;
            const matchA = a.match(regex);
            const matchB = b.match(regex);
            if (matchA && matchB) {
                const priceA = parseInt(matchA[1].replace(/,/g, ''), 10);
                const priceB = parseInt(matchB[1].replace(/,/g, ''), 10);
                const cat2A = matchA[2];
                const cat2B = matchB[2];
                if (priceA !== priceB) return priceA - priceB;
                return cat2A.localeCompare(cat2B);
            }
            return a.localeCompare(b);
        });
    } else if (chartType === PACKAGE_MANAGER_TYPE.LEVEL) {
        yAxisData = [...yCategorySet].sort((a, b) => {
            const getSortValue = (levelString) => {
                const match = levelString.match(/Lv (\d+)/);
                return match ? parseInt(match[1], 10) : Infinity;
            };
            return getSortValue(a) - getSortValue(b);
        });
    } else {
        yAxisData = [...yCategorySet].sort();
    }

    const seriesDataForChart = [];
    const originalDataMap = new Map();

    yAxisData.forEach((yCategory, yIdx) => {
        xAxisData.forEach((date, xIdx) => {
            const categoryDataForDate = countsByDateAndYAxis[date]?.[yCategory];
            let count = categoryDataForDate?.count || 0;
            let amount = categoryDataForDate?.amount || 0;
            let valueForColoringAndLabel, percentageForLabel;
            let countForTooltip = count, amountForTooltip = amount;

            switch (chartType) {
                case PACKAGE_MANAGER_TYPE.AMOUNT:
                    valueForColoringAndLabel = amount;
                    const totalAmountOnDate = totalsByDate[date]?.amount || 0;
                    percentageForLabel = totalAmountOnDate > 0 ? parseFloat(((amount / totalAmountOnDate) * 100).toFixed(2)) : 0;
                    break;
                default:
                    valueForColoringAndLabel = count;
                    const totalCountOnDate = totalsByDate[date]?.count || 0;
                    percentageForLabel = totalCountOnDate > 0 ? parseFloat(((count / totalCountOnDate) * 100).toFixed(2)) : 0;
                    break;
            }

            const originalDataPoint = {
                count: countForTooltip,
                amount: amountForTooltip,
                percentage: percentageForLabel,
                value: valueForColoringAndLabel
            };

            originalDataMap.set(`${xIdx}-${yIdx}`, originalDataPoint);
            seriesDataForChart.push([xIdx, yIdx, valueForColoringAndLabel]);
        });
    });

    return { xAxisData, yAxisData, seriesDataForChart, originalDataMap };
}

const EChartsCohortChart = ({
                                chartType = PACKAGE_MANAGER_TYPE.COUNT,
                                externalData = [],
                                category1FilterFromProps = null
                            }) => {
    const { darkMode } = useContext(DarkModeContext);
    const { token } = theme.useToken();
    const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.PERCENT);
    const [currentCategory1Filter, setCurrentCategory1Filter] = useState(category1FilterFromProps);
    const [csvData, setCsvData] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const category1Options = useMemo(() => {
        if (!externalData || externalData.length === 0) return [];
        return [...new Set(externalData.filter(item => item.category1).map(item => item.category1))].sort();
    }, [externalData]);

    useEffect(() => {
        if (chartType === PACKAGE_MANAGER_TYPE.RANGE) {
            if (category1FilterFromProps !== null) setCurrentCategory1Filter(category1FilterFromProps);
            else if (category1Options.length > 0) setCurrentCategory1Filter(category1Options[0]);
            else setCurrentCategory1Filter(null);
        } else setCurrentCategory1Filter(null);
    }, [category1FilterFromProps, category1Options, chartType]);

    const { xAxisData, yAxisData, seriesDataForChart, originalDataMap } = useMemo(() => {
        return processDataForECharts(externalData, chartType, currentCategory1Filter);
    }, [externalData, chartType, currentCategory1Filter]);

    const finalSeriesData = useMemo(() => {
        return seriesDataForChart.map(item => {
            const xIdx = item[0];
            const yIdx = item[1];
            const originalPoint = originalDataMap.get(`${xIdx}-${yIdx}`);
            if (!originalPoint) return [xIdx, yIdx, 0];

            const displayValue = displayMode === DISPLAY_MODE.PERCENT ? originalPoint.percentage : originalPoint.value;
            return [xIdx, yIdx, displayValue];
        });
    }, [seriesDataForChart, originalDataMap, displayMode]);

    const handleCategory1FilterChange = (value) => setCurrentCategory1Filter(value || null);

    const getChartTitleText = () => {
        let title = '';
        switch (chartType) {
            case PACKAGE_MANAGER_TYPE.COUNT:
                title = '패키지별 구매 수';
                break;
            case PACKAGE_MANAGER_TYPE.AMOUNT:
                title = '패키지별 판매액';
                break;
            case PACKAGE_MANAGER_TYPE.RANGE:
                title = `가격대별 구매 수 ${currentCategory1Filter ? `(${currentCategory1Filter})` : ''}`;
                break;
            case PACKAGE_MANAGER_TYPE.LEVEL:
                title = `구매 유저 레벨 분포`;
                break;
            default:
                title = '코호트 분석';
        }
        return title;
    };

    const getChartIcon = () => {
        switch (chartType) {
            case PACKAGE_MANAGER_TYPE.AMOUNT:
                return <DollarOutlined />;
            case PACKAGE_MANAGER_TYPE.RANGE:
                return <FilterOutlined />;
            case PACKAGE_MANAGER_TYPE.LEVEL:
                return <PieChartOutlined />;
            default:
                return <BarChartOutlined />;
        }
    };

    const handleFullscreen = () => {
        setIsFullscreen(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ESC 키로 전체화면 종료
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        if (isFullscreen) {
            document.addEventListener('keydown', handleEsc);
            return () => document.removeEventListener('keydown', handleEsc);
        }
    }, [isFullscreen]);

    useEffect(() => {
        if (!xAxisData || !yAxisData || xAxisData.length === 0 || yAxisData.length === 0 || !originalDataMap) {
            setCsvData([]);
            return;
        }

        const headers = ["분류 (Y축)", ...xAxisData];
        const dataForCsv = yAxisData.map((yCategory, yIdx) => {
            const row = { "분류 (Y축)": yCategory };
            xAxisData.forEach((date, xIdx) => {
                const dataPoint = originalDataMap.get(`${xIdx}-${yIdx}`);
                if (dataPoint) {
                    if (displayMode === DISPLAY_MODE.PERCENT) {
                        row[date] = `${dataPoint.percentage.toFixed(1)}%`;
                    } else {
                        row[date] = (chartType === PACKAGE_MANAGER_TYPE.AMOUNT) ? dataPoint.amount : dataPoint.count;
                    }
                } else {
                    row[date] = displayMode === DISPLAY_MODE.PERCENT ? '0.0%' : 0;
                }
            });
            return row;
        });
        setCsvData([headers, ...dataForCsv.map(row => Object.values(row))]);
    }, [xAxisData, yAxisData, originalDataMap, displayMode, chartType]);

    const dynamicChartHeight = useMemo(() => {
        if (yAxisData.length === 0) return MIN_CHART_HEIGHT;
        const calculatedHeight = TITLE_HEIGHT + X_AXIS_HEIGHT + (yAxisData.length * FIXED_CELL_HEIGHT) + CHART_BOTTOM_PADDING;
        return Math.max(calculatedHeight, MIN_CHART_HEIGHT);
    }, [yAxisData]);

    const dynamicChartWidth = useMemo(() => {
        if (xAxisData.length === 0) return '100%';
        return (xAxisData.length * FIXED_CELL_WIDTH) + Y_AXIS_LABEL_WIDTH + CHART_RIGHT_PADDING;
    }, [xAxisData]);

    const getOption = () => {
        let visualMapMax = 100;
        if (displayMode === DISPLAY_MODE.VALUE && finalSeriesData.length > 0) {
            const valuesForMax = finalSeriesData.map(item => typeof item[2] === 'number' ? item[2] : 0);
            const maxVal = Math.max(...valuesForMax, 0);
            visualMapMax = maxVal === 0 ? 1 : maxVal;
        }

        return {
            tooltip: {
                position: function (point, params, dom, rect, size) {
                    // 화면 중앙에 위치하도록 조정, 뷰포트 밖으로 나가지 않게
                    const [x, y] = point;
                    const vw = window.innerWidth;
                    const vh = window.innerHeight;
                    const domRect = dom.getBoundingClientRect();
                    let left = x;
                    let top = y;
                    if (left + domRect.width > vw) left = vw - domRect.width - 10;
                    if (top + domRect.height > vh) top = vh - domRect.height - 10;
                    if (left < 0) left = 10;
                    if (top < 0) top = 10;
                    return [left, top];
                },
                confine: true, // 툴팁이 차트 영역을 벗어나지 않도록
                backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
                borderColor: token.colorBorder,
                textStyle: {
                    color: token.colorText
                },
                formatter: (params) => {
                    if (!params.value || params.value.length < 2) return '';
                    const xIndex = params.value[0];
                    const yIndex = params.value[1];
                    const dataPoint = originalDataMap.get(`${xIndex}-${yIndex}`);
                    if (!dataPoint) return '';

                    let tooltipText = `<div style="font-weight: bold; margin-bottom: 8px; color: ${token.colorPrimary};">${yAxisData[yIndex]} - ${xAxisData[xIndex]}</div>`;
                    tooltipText += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
                    tooltipText += `<span style="display: inline-block; width: 8px; height: 8px; background: ${token.colorSuccess}; border-radius: 50%; margin-right: 6px;"></span>`;
                    tooltipText += `구매수: <strong>${dataPoint.count.toLocaleString()}</strong></div>`;

                    if (chartType === PACKAGE_MANAGER_TYPE.AMOUNT || chartType === PACKAGE_MANAGER_TYPE.RANGE) {
                        tooltipText += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
                        tooltipText += `<span style="display: inline-block; width: 8px; height: 8px; background: ${token.colorWarning}; border-radius: 50%; margin-right: 6px;"></span>`;
                        tooltipText += `판매액: <strong>${dataPoint.amount?.toLocaleString()}원</strong></div>`;
                    }

                    tooltipText += `<div style="display: flex; align-items: center;">`;
                    tooltipText += `<span style="display: inline-block; width: 8px; height: 8px; background: ${token.colorInfo}; border-radius: 50%; margin-right: 6px;"></span>`;
                    tooltipText += `비율: <strong>${dataPoint.percentage.toFixed(1)}%</strong></div>`;
                    return tooltipText;
                }
            },
            grid: {
                left: Y_AXIS_LABEL_WIDTH + 40, // y축 레이블이 잘리지 않도록 여유 공간 확보
                top: X_AXIS_HEIGHT,
                right: CHART_RIGHT_PADDING,
                bottom: CHART_BOTTOM_PADDING
            },
            xAxis: {
                type: 'category',
                data: xAxisData,
                position: 'top',
                splitArea: { show: true },
                axisLabel: {
                    rotate: 0,
                    color: token.colorText
                },
                axisLine: {
                    lineStyle: {
                        color: token.colorBorder
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: yAxisData,
                splitArea: { show: true },
                axisLabel: {
                    interval: 0,
                    color: token.colorText,
                    width: Y_AXIS_LABEL_WIDTH + 40,
                    overflow: 'break', // 너무 길면 줄바꿈
                    formatter: function(value) {
                        // 2줄 이상이면 ... 처리
                        if (value.length > 18) return value.slice(0, 17) + '...';
                        return value;
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: token.colorBorder
                    }
                }
            },
            visualMap: {
                show: false,
                min: 0,
                max: visualMapMax,
                calculable: false,
                orient: 'horizontal',
                left: 'center',
                bottom: '0%',
                inRange: {
                    color: darkMode ?
                        ['#0f1419', '#1e3a8a', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'] :
                        ['#f0f9ff', '#cce7ff', '#99cfff', '#66b7ff', '#339eff', '#0086ff']
                },
            },
            series: [{
                name: '코호트 데이터',
                type: 'heatmap',
                data: finalSeriesData,
                label: {
                    show: true,
                    formatter: (params) => {
                        if (!params.value || params.value.length < 2) return '';
                        const xIndex = params.value[0];
                        const yIndex = params.value[1];
                        const dataPoint = originalDataMap.get(`${xIndex}-${yIndex}`);
                        if (!dataPoint) return '';

                        let labelValue;
                        if (displayMode === DISPLAY_MODE.PERCENT) {
                            labelValue = dataPoint.percentage;
                            return labelValue > 0 ? `${labelValue.toFixed(1)}%` : '';
                        } else {
                            labelValue = (chartType === PACKAGE_MANAGER_TYPE.AMOUNT) ? dataPoint.amount : dataPoint.count;
                            return labelValue > 0 ? labelValue.toLocaleString() : '';
                        }
                    },
                    color: darkMode ? '#ffffff' : '#000000',
                    fontSize: 11
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 15,
                        shadowColor: token.colorPrimary + '40',
                        borderColor: token.colorPrimary,
                        borderWidth: 2
                    }
                },
            }]
        };
    };

    const getCsvFileName = () => {
        const typeName = Object.keys(PACKAGE_MANAGER_TYPE).find(key => PACKAGE_MANAGER_TYPE[key] === chartType) || 'Cohort';
        const modeName = displayMode === DISPLAY_MODE.PERCENT ? '비율' : '값';
        const filterName = (chartType === PACKAGE_MANAGER_TYPE.RANGE && currentCategory1Filter) ? `_${currentCategory1Filter}` : '';
        const date = dayjs().format('YYYYMMDD');
        return `${typeName}${filterName}_${modeName}_${date}.csv`;
    };

    const hasData = xAxisData && yAxisData && xAxisData.length > 0 && yAxisData.length > 0;

    // 전체화면 모드일 때 별도 렌더링
    if (isFullscreen) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                background: token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* 전체화면 헤더 */}
                <div style={{
                    background: `linear-gradient(135deg, ${token.colorPrimary}08, ${token.colorSuccess}05)`,
                    padding: '16px 24px',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Space align="center" size="middle">
                        <div style={{
                            background: token.colorPrimary,
                            borderRadius: '8px',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {getChartIcon()}
                        </div>
                        <div>
                            <Title level={4} style={{
                                margin: 0,
                                color: token.colorText,
                                fontWeight: 600
                            }}>
                                {getChartTitleText()}
                            </Title>
                            <Space size="small" style={{ marginTop: '4px' }}>
                                <Tag color={displayMode === DISPLAY_MODE.PERCENT ? 'blue' : 'green'}>
                                    {displayMode === DISPLAY_MODE.PERCENT ? '비율 모드' : '값 모드'}
                                </Tag>
                                {currentCategory1Filter && (
                                    <Tag color="orange">
                                        {currentCategory1Filter}
                                    </Tag>
                                )}
                            </Space>
                        </div>
                    </Space>

                    <Space size="middle">
                        {csvData && csvData.length > 1 && (
                            <CSVLink
                                data={csvData}
                                filename={getCsvFileName()}
                                style={{ textDecoration: 'none' }}
                            >
                                <Tooltip title="CSV 다운로드">
                                    <Button
                                        type="primary"
                                        icon={<FileExcelOutlined />}
                                        size="small"
                                    >
                                        CSV 다운로드
                                    </Button>
                                </Tooltip>
                            </CSVLink>
                        )}
                        <Tooltip title="전체화면 종료 (ESC)">
                            <Button
                                type="default"
                                icon={<CloseOutlined />}
                                onClick={() => setIsFullscreen(false)}
                            >
                                닫기
                            </Button>
                        </Tooltip>
                    </Space>
                </div>

                {/* 전체화면 차트 */}
                <div style={{
                    flex: 1,
                    padding: '16px',
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    {hasData ? (
                        <ReactECharts
                            option={getOption()}
                            style={{
                                width: '100%',
                                height: dynamicChartHeight,
                                minHeight: '400px'
                            }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Space direction="vertical">
                                    <Text type="secondary">차트를 표시할 데이터가 없습니다</Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        선택된 필터에 해당하는 데이터가 없거나 데이터가 로드되지 않았습니다
                                    </Text>
                                </Space>
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <Card
            className={`echarts-cohort-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            style={{
                background: token.colorBgContainer,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                overflow: 'hidden',
                padding: 0,
                marginBottom: '16px',
            }}
        >
            {/* 헤더 섹션 */}
            <div style={{
                background: `linear-gradient(135deg, ${token.colorPrimary}08, ${token.colorSuccess}05)`,
                padding: '24px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`
            }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space align="center" size="middle">
                            <div style={{
                                background: token.colorPrimary,
                                borderRadius: '8px',
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {getChartIcon()}
                            </div>
                            <div>
                                <Title level={4} style={{
                                    margin: 0,
                                    color: token.colorText,
                                    fontWeight: 600
                                }}>
                                    {getChartTitleText()}
                                </Title>
                                <Space size="small" style={{ marginTop: '4px' }}>
                                    <Tag color={displayMode === DISPLAY_MODE.PERCENT ? 'blue' : 'green'}>
                                        {displayMode === DISPLAY_MODE.PERCENT ? '비율 모드' : '값 모드'}
                                    </Tag>
                                    {currentCategory1Filter && (
                                        <Tag color="orange">
                                            {currentCategory1Filter}
                                        </Tag>
                                    )}
                                </Space>
                            </div>
                        </Space>
                    </Col>

                    <Col>
                        <Space size="small">
                            <Tooltip title="전체화면">
                                <Button
                                    icon={<FullscreenOutlined />}
                                    onClick={handleFullscreen}
                                    size="small"
                                />
                            </Tooltip>

                            {csvData && csvData.length > 1 && (
                                <CSVLink
                                    data={csvData}
                                    filename={getCsvFileName()}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Tooltip title="CSV 다운로드">
                                        <Button
                                            type="primary"
                                            icon={<FileExcelOutlined />}
                                            size="small"
                                        >
                                            CSV
                                        </Button>
                                    </Tooltip>
                                </CSVLink>
                            )}
                        </Space>
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '24px' }}>
                {/* 컨트롤 패널 */}
                <Card
                    size="small"
                    title={
                        <Space>
                            <FilterOutlined />
                            <Text strong>차트 설정</Text>
                        </Space>
                    }
                    style={{ marginBottom: '24px' }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>표시 모드</Text>
                                <Radio.Group
                                    value={displayMode}
                                    onChange={(e) => setDisplayMode(e.target.value)}
                                    buttonStyle="solid"
                                    size="small"
                                >
                                    <Radio.Button value={DISPLAY_MODE.PERCENT}>
                                        <Space>
                                            <EyeOutlined />
                                            비율(%)
                                        </Space>
                                    </Radio.Button>
                                    <Radio.Button value={DISPLAY_MODE.VALUE}>
                                        <Space>
                                            <BarChartOutlined />
                                            값
                                        </Space>
                                    </Radio.Button>
                                </Radio.Group>
                            </Space>
                        </Col>

                        {chartType === PACKAGE_MANAGER_TYPE.RANGE && category1Options.length > 0 && (
                            <Col xs={24} sm={12} md={8}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>카테고리 필터</Text>
                                    <Select
                                        value={currentCategory1Filter}
                                        onChange={handleCategory1FilterChange}
                                        placeholder="카테고리 선택"
                                        style={{ width: '100%' }}
                                        size="small"
                                        allowClear
                                    >
                                        {category1Options.map(cat => (
                                            <Option key={cat} value={cat}>{cat}</Option>
                                        ))}
                                    </Select>
                                </Space>
                            </Col>
                        )}
                    </Row>
                </Card>

                {/* 차트 영역 */}
                <Card
                    size="small"
                    title={
                        <Space>
                            <BarChartOutlined />
                            <Text strong>히트맵 차트</Text>
                        </Space>
                    }
                    style={{
                        background: token.colorFillAlter,
                        border: `1px solid ${token.colorBorder}`
                    }}
                >
                    {hasData ? (
                        <div style={{
                            background: token.colorBgContainer,
                            borderRadius: token.borderRadius,
                            padding: '16px',
                            overflow: 'auto'
                        }}>
                            <ReactECharts
                                option={getOption()}
                                style={{
                                    width: dynamicChartWidth,
                                    height: dynamicChartHeight,
                                    minWidth: '100%'
                                }}
                                notMerge={true}
                                lazyUpdate={true}
                            />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical">
                                        <Text type="secondary">차트를 표시할 데이터가 없습니다</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            선택된 필터에 해당하는 데이터가 없거나 데이터가 로드되지 않았습니다
                                        </Text>
                                    </Space>
                                }
                            />
                        </div>
                    )}
                </Card>
            </div>
        </Card>
    );
};

export default EChartsCohortChart;

